import { has, isEqual, omit } from "lodash";
import { combineEpics, Epic, ofType } from "redux-observable";
import { EMPTY, from, merge, Observable, of } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { AppStore } from "..";
import { getOption } from "../../utils/utils";
import { AuthActions } from "../auth/actions";
import {
  Fetchable,
  FetchableError,
  FetchableSuccess,
  isFetchableError,
} from "../common/fetchable/helpers";
import { UserActions } from "./actions";
import { UsersDTO } from "./models/dto";
import { UsersService } from "./service";

const getUserEpic: Epic<UserActions.Actions, UserActions.Actions, AppStore> = (
  action$,
  store$
) =>
  action$.pipe(
    ofType(UserActions.ActionTypes.getUserDetails),
    switchMap<
      UserActions.Actions,
      Observable<
        FetchableError | FetchableSuccess<UsersDTO.Responses.GetSingle.T>
      >
    >(() =>
      getOption(store$.value.auth.login)
        .filter((x) => x.accessToken.authenticated)
        .fold(
          () =>
            of<FetchableError>({
              status: "error",
              err: new Error("No user ID"),
            }),
          (token) =>
            UsersService.getUserDetails(
              token.accessToken.originalUser?.type === "DELEGATE"
                ? token.accessToken.originalUser.id
                : token.accessToken.credentialsId
            )
        )
    ),
    map((x) => UserActions.setUserDetails(x))
  );

const updateUserEpic: Epic<
  UserActions.Actions,
  UserActions.Actions,
  AppStore
> = (action$, store$) =>
  action$.pipe(
    ofType(UserActions.ActionTypes.postUserDetails),
    switchMap<
      UserActions.PostUserDetails,
      Observable<
        FetchableError | FetchableSuccess<UsersDTO.Responses.GetSingle.T>
      >
    >(({ payload }) =>
      getOption(store$.value.auth.login)
        .filter((x) => x.accessToken.authenticated)
        .fold(
          () =>
            of({
              status: "error",
              err: new Error("No user ID"),
            }),
          (token) =>
            UsersService.editUserDetails(
              token.accessToken.originalUser?.type === "DELEGATE"
                ? token.accessToken.originalUser.id
                : token.accessToken.credentialsId,
              omit(payload, "emailAddress")
            )
        )
    ),
    switchMap((x) =>
      isFetchableError(x) ? EMPTY : of(UserActions.getUserDetails())
    )
  );

const getAllUsersEpic: Epic<UserActions.Actions> = (action$, state$) =>
  action$.pipe(
    ofType(UserActions.ActionTypes.getUsers),
    withLatestFrom(state$),
    switchMap<
      [UserActions.Actions, AppStore],
      Observable<FetchableError | FetchableSuccess<UsersDTO.Responses.GetAll.T>>
    >(([_, store]) =>
      UsersService.getAllUsers(
        store.users.allUsers.pagination || {
          pagination: { size: 0, page: 0 },
        }
      )
    ),
    map((x) => UserActions.setUsers(x))
  );

const setAllUsersPaginationEpic: Epic<UserActions.Actions> = (action$) =>
  action$.pipe(
    ofType(UserActions.ActionTypes.setPagination),
    switchMap<
      UserActions.SetPagination,
      Observable<FetchableError | FetchableSuccess<UsersDTO.Responses.GetAll.T>>
    >((action) => UsersService.getAllUsers(action.payload)),
    map((x) => UserActions.setUsers(x))
  );

const getSingleUser: Epic<UserActions.Actions, UserActions.Actions> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<UserActions.Actions, UserActions.GetSingleUser>(
      UserActions.ActionTypes.getSingleUser
    ),
    withLatestFrom(state$),
    switchMap<
      [UserActions.GetSingleUser, AppStore],
      Observable<Fetchable<UsersDTO.Responses.GetSingle.T>>
    >(
      ([
        {
          payload: { userId, refetch },
        },
        store,
      ]) => {
        if (
          !refetch &&
          has(store.users.allUsersMap, userId) &&
          store.users.allUsersMap[userId] !== "LOADING"
        ) {
          return of({
            status: "ready",
            data: store.users.allUsersMap[
              userId
            ] as UsersDTO.Responses.GetSingle.T,
          });
        }

        return merge(
          of({
            status: "loading",
          }),
          from(
            UsersService.getUser(userId)
              .then<FetchableSuccess<UsersDTO.Responses.GetSingle.T>>(
                (response) => ({
                  status: "ready",
                  data: response,
                })
              )
              .catch<FetchableError>((err) => ({
                status: "error",
                err,
              }))
          )
        );
      }
    ),
    map(UserActions.setSingleUser)
  );

const dependencies: Epic<
  AuthActions.Actions | UserActions.Actions,
  UserActions.Actions
> = (action$) =>
  action$.pipe(
    ofType(AuthActions.ActionTypes.authDone),
    switchMap(() =>
      from([UserActions.resetUserDetails(), UserActions.resetUsers()])
    )
  );

export const usersEpic = combineEpics(
  getUserEpic,
  updateUserEpic,
  getAllUsersEpic,
  setAllUsersPaginationEpic,
  getSingleUser,
  dependencies
);
